import { forwardRef, HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'streak' | 'level';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  animate?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-cream-100 text-charcoal-500 border-cream-300',
  primary: 'bg-primary-50 text-primary-600 border-primary-200',
  success: 'bg-mint-50 text-charcoal-600 border-mint-200',
  warning: 'bg-coral-50 text-charcoal-600 border-coral-200',
  error: 'bg-rose-50 text-charcoal-600 border-rose-200',
  streak: 'bg-gradient-to-r from-coral-300 to-coral-400 text-white border-transparent',
  level: 'bg-primary-400 text-white border-transparent',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      children,
      variant = 'default',
      size = 'md',
      icon,
      animate = false,
      className,
      ...props
    },
    ref
  ) => {
    const badgeClasses = clsx(
      'inline-flex items-center gap-1.5 font-medium rounded-full border whitespace-nowrap',
      variantStyles[variant],
      sizeStyles[size],
      className
    );

    const content = (
      <>
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children}
      </>
    );

    if (animate) {
      return (
        <motion.span
          ref={ref}
          className={badgeClasses}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          {content}
        </motion.span>
      );
    }

    return (
      <span ref={ref} className={badgeClasses} {...props}>
        {content}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Streak Badge - special styled badge for streaks
interface StreakBadgeProps {
  days: number;
  animate?: boolean;
  className?: string;
}

export const StreakBadge = ({ days, animate = false, className }: StreakBadgeProps) => {
  return (
    <Badge
      variant="streak"
      size="md"
      icon={<span className="text-base">🔥</span>}
      animate={animate}
      className={className}
    >
      {days} day{days !== 1 ? 's' : ''}
    </Badge>
  );
};

// Level Badge
interface LevelBadgeProps {
  level: number;
  name: string;
  icon?: string;
  animate?: boolean;
  className?: string;
}

export const LevelBadge = ({
  level,
  name,
  icon = '🌱',
  animate = false,
  className,
}: LevelBadgeProps) => {
  return (
    <Badge
      variant="level"
      size="lg"
      icon={<span className="text-lg">{icon}</span>}
      animate={animate}
      className={className}
    >
      Level {level} • {name}
    </Badge>
  );
};

// Achievement Badge
interface AchievementBadgeProps {
  name: string;
  icon: string;
  earned?: boolean;
  animate?: boolean;
  className?: string;
}

export const AchievementBadge = ({
  name,
  icon,
  earned = false,
  animate = false,
  className,
}: AchievementBadgeProps) => {
  return (
    <Badge
      variant={earned ? 'primary' : 'default'}
      size="md"
      icon={<span className="text-base">{icon}</span>}
      animate={animate && earned}
      className={clsx(!earned && 'opacity-50 grayscale', className)}
    >
      {name}
    </Badge>
  );
};

// XP Badge
interface XPBadgeProps {
  amount: number;
  animate?: boolean;
  className?: string;
}

export const XPBadge = ({ amount, animate = false, className }: XPBadgeProps) => {
  const isPositive = amount > 0;
  return (
    <Badge
      variant={isPositive ? 'success' : 'default'}
      size="sm"
      animate={animate}
      className={className}
    >
      {isPositive ? '+' : ''}{amount} XP
    </Badge>
  );
};

export default Badge;
